"use client"

import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import { resetChatwootSdkRunGuard, runChatwootSdkOnce } from "@/lib/chatwoot/run-sdk-once"
import {
  resolveChatwootPublicConfig,
  warnIfChatwootPublicConfigIncomplete,
} from "@/lib/chatwoot/public-widget"
import { isCadastroEntrarSupportInlinePath } from "@/lib/support/inline-support-paths"
import { waitUntilAfterHero } from "@/lib/ui/wait-until-after-hero"

/** Rotas onde o balão de chat não aparece (admin / render). */
const HIDDEN_PREFIXES = ["/admin", "/render"] as const

/** Alinhado a `lg` do Tailwind — mesmo breakpoint do card inline de suporte. */
const INLINE_SUPPORT_MOBILE_MAX_PX = 1023

/** Caminho do SDK (self-hosted ou cloud). */
export const SDK_PATH = "/packs/js/sdk.js"

/** Igual ao bloco `window.chatwootSettings` do script do painel Chatwoot; `locale` em pt-BR no app. */
const CHATWOOT_WIDGET_UI = {
  position: "right" as const,
  type: "standard" as const,
  launcherTitle: "",
  locale: "pt_BR",
}

/**
 * Textos opcionais do widget (SDK). Ver docs Chatwoot: welcome*, availableMessage, unavailableMessage.
 * Valor vazio ou `__HIDE__` envia zero-width space — o SDK trata string vazia como “usar padrão”, então
 * precisamos de um caractere invisível para “sumir” com o texto.
 * O subtítulo tipo “Normalmente responde em …” vem do tempo de resposta da inbox no painel Chatwoot, não do SDK.
 */
function parseChatwootTextEnv(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined
  const t = raw.trim()
  if (t === "" || t === "__HIDE__") return "\u200b"
  return raw
}

function buildChatwootSettings() {
  const welcomeTitle = parseChatwootTextEnv(process.env.NEXT_PUBLIC_CHATWOOT_WELCOME_TITLE)
  const welcomeDescription = parseChatwootTextEnv(
    process.env.NEXT_PUBLIC_CHATWOOT_WELCOME_DESCRIPTION,
  )
  const availableMessage = parseChatwootTextEnv(process.env.NEXT_PUBLIC_CHATWOOT_AVAILABLE_MESSAGE)
  const unavailableMessage = parseChatwootTextEnv(
    process.env.NEXT_PUBLIC_CHATWOOT_UNAVAILABLE_MESSAGE,
  )

  return {
    ...CHATWOOT_WIDGET_UI,
    ...(welcomeTitle !== undefined ? { welcomeTitle } : {}),
    ...(welcomeDescription !== undefined ? { welcomeDescription } : {}),
    ...(availableMessage !== undefined ? { availableMessage } : {}),
    ...(unavailableMessage !== undefined ? { unavailableMessage } : {}),
  }
}

function isHiddenPath(pathname: string | null): boolean {
  if (!pathname) return false
  return HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))
}

function shouldHideBubbleForInlineSupport(pathname: string | null): boolean {
  if (!isCadastroEntrarSupportInlinePath(pathname)) return false
  if (typeof window === "undefined") return false
  return window.matchMedia(`(max-width: ${INLINE_SUPPORT_MOBILE_MAX_PX}px)`).matches
}

function isBubbleHidden(pathname: string | null): boolean {
  return isHiddenPath(pathname) || shouldHideBubbleForInlineSupport(pathname)
}

/** Mesmo breakpoint do SDK Chatwoot (IFrameHelper.toggleCloseButton). */
const CHATWOOT_MOBILE_MAX_PX = 668

let bodyScrollLockY: number | null = null

function lockBodyScrollForChatwoot() {
  if (bodyScrollLockY !== null) return
  if (!window.matchMedia(`(max-width: ${CHATWOOT_MOBILE_MAX_PX}px)`).matches) return
  bodyScrollLockY = window.scrollY
  document.documentElement.style.overflow = "hidden"
  document.body.style.overflow = "hidden"
  document.body.style.position = "fixed"
  document.body.style.top = `-${bodyScrollLockY}px`
  document.body.style.left = "0"
  document.body.style.right = "0"
  document.body.style.width = "100%"
  document.documentElement.classList.add("chatwoot-widget-open")
}

function unlockBodyScrollForChatwoot() {
  if (bodyScrollLockY === null) {
    document.documentElement.classList.remove("chatwoot-widget-open")
    return
  }
  const y = bodyScrollLockY
  bodyScrollLockY = null
  document.documentElement.style.overflow = ""
  document.body.style.overflow = ""
  document.body.style.position = ""
  document.body.style.top = ""
  document.body.style.left = ""
  document.body.style.right = ""
  document.body.style.width = ""
  document.documentElement.classList.remove("chatwoot-widget-open")
  window.scrollTo(0, y)
}

declare global {
  interface Window {
    /** UI/locale (snippet do painel); token/baseUrl vão em `chatwootSDK.run()`. */
    chatwootSettings?: {
      position?: "left" | "right"
      type?: string
      launcherTitle?: string
      locale?: string
      welcomeTitle?: string
      welcomeDescription?: string
      availableMessage?: string
      unavailableMessage?: string
      hideMessageBubble?: boolean
    }
    chatwootSDK?: {
      run: (opts: { baseUrl: string; websiteToken: string }) => void
    }
    $chatwoot?: {
      toggleBubbleVisibility?: (state: "hide" | "show") => void
      toggle?: (state: "open" | "close") => void
      setUser?: (identifier: string, attrs: { name?: string; email?: string }) => void
      setCustomAttributes?: (attrs: Record<string, string | number | boolean>) => void
    }
    __CHATWOOT_SDK_LOADED__?: boolean
  }
}

/**
 * Carrega o widget de live chat do Chatwoot (Website channel) uma vez.
 * Requer NEXT_PUBLIC_CHATWOOT_ENABLED=true e token/baseUrl em env.
 */
export function ChatwootWidget() {
  const pathname = usePathname()
  const { enabled, baseUrl, websiteToken } = resolveChatwootPublicConfig()
  const [isReadyToLoad, setIsReadyToLoad] = useState(false)

  useEffect(() => {
    warnIfChatwootPublicConfigIncomplete()
  }, [])

  useEffect(() => {
    if (!enabled || !baseUrl || !websiteToken) return
    return waitUntilAfterHero(() => setIsReadyToLoad(true))
  }, [enabled, baseUrl, websiteToken])

  useEffect(() => {
    if (!enabled || !baseUrl || !websiteToken || !isReadyToLoad) return

    if (isHiddenPath(pathname)) {
      window.$chatwoot?.toggleBubbleVisibility?.("hide")
      unlockBodyScrollForChatwoot()
      return
    }

    const applyInlineSupportVisibility = () => {
      if (shouldHideBubbleForInlineSupport(pathname)) {
        window.$chatwoot?.toggleBubbleVisibility?.("hide")
        unlockBodyScrollForChatwoot()
        return true
      }
      return false
    }

    const sdkSrc = `${baseUrl}${SDK_PATH}`
    let existing = document.querySelector<HTMLScriptElement>(`script[src="${sdkSrc}"]`)

    // Script morto (ex.: 404/rede): remove e tenta de novo no próximo ciclo.
    if (
      existing &&
      !window.$chatwoot &&
      !window.chatwootSDK &&
      !window.__CHATWOOT_SDK_LOADED__
    ) {
      existing.remove()
      resetChatwootSdkRunGuard()
      existing = null
    }

    if (existing) {
      if (window.$chatwoot) {
        if (!applyInlineSupportVisibility()) {
          window.$chatwoot.toggleBubbleVisibility?.("show")
        }
      } else if (window.chatwootSDK) {
        window.chatwootSettings = buildChatwootSettings()
        runChatwootSdkOnce({ websiteToken, baseUrl })
        applyInlineSupportVisibility()
      }
    } else {
      // Mesma ordem do snippet do painel: `chatwootSettings` antes do script; `run` no onload.
      window.chatwootSettings = buildChatwootSettings()

      const script = document.createElement("script")
      script.src = sdkSrc
      script.async = true
      script.onload = () => {
        window.__CHATWOOT_SDK_LOADED__ = true
        runChatwootSdkOnce({ websiteToken, baseUrl })
        applyInlineSupportVisibility()
      }
      script.onerror = () => {
        window.__CHATWOOT_SDK_LOADED__ = false
        resetChatwootSdkRunGuard()
        script.remove()
        if (process.env.NODE_ENV !== "production") {
          console.warn("[chatwoot] Falha ao carregar o SDK (rede/404). Retry no próximo mount/navegação.")
        }
      }
      document.head.appendChild(script)
    }

    const mq = window.matchMedia(`(max-width: ${INLINE_SUPPORT_MOBILE_MAX_PX}px)`)
    const onViewportChange = () => {
      if (isHiddenPath(pathname)) {
        window.$chatwoot?.toggleBubbleVisibility?.("hide")
        return
      }
      if (shouldHideBubbleForInlineSupport(pathname)) {
        window.$chatwoot?.toggleBubbleVisibility?.("hide")
        unlockBodyScrollForChatwoot()
      } else {
        window.$chatwoot?.toggleBubbleVisibility?.("show")
      }
    }
    mq.addEventListener("change", onViewportChange)

    return () => {
      mq.removeEventListener("change", onViewportChange)
    }
  }, [enabled, baseUrl, websiteToken, pathname, isReadyToLoad])

  useEffect(() => {
    if (!enabled || !baseUrl || !websiteToken || !isReadyToLoad) return

    const onOpened = () => {
      if (isBubbleHidden(pathname)) return
      lockBodyScrollForChatwoot()
    }

    const onClosed = () => {
      unlockBodyScrollForChatwoot()
    }

    window.addEventListener("chatwoot:opened", onOpened)
    window.addEventListener("chatwoot:closed", onClosed)

    return () => {
      window.removeEventListener("chatwoot:opened", onOpened)
      window.removeEventListener("chatwoot:closed", onClosed)
      unlockBodyScrollForChatwoot()
    }
  }, [enabled, baseUrl, websiteToken, pathname, isReadyToLoad])

  return null
}
